'use client';

// Panel de acciones del MÓDULO 5 (Segunda instancia). Se muestra dentro de la
// pestaña "Segunda instancia" del detalle de expediente cuando el estado está en
// SEGUNDA_INSTANCIA / RECURSO_INTERPUESTO. Reutiliza el patrón de "tarjeta de
// acción -> Dialog con formulario mínimo" de PanelJuzgamiento. Cada acción refresca
// el detalle al terminar y muestra toasts. Los bloqueos procesales del backend
// llegan como error 422 con {message}; se muestran. RBAC por rol de localStorage.

import { useEffect, useState } from 'react';
import { FileInput, Gavel, Scale, Send, ShieldCheck } from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import type { ExpedienteDetalle as ExpDetalle } from '@/lib/expedientes';
import { api } from '@/lib/api';
import {
  recepcionSegundaInstancia, repartoSegundaInstancia, decisionSegundaInstancia,
  notificarSegundaInstancia, ejecutoriaSegundaInstancia,
  idSegundaInstancia, tieneSegundaInstancia,
  puedeRepartir2a, puedeDecidir2a, usuarioActual,
  DECISION_SEGUNDA, RETORNA_A,
  type DecisionSegunda, type RetornaA,
} from '@/lib/segunda-instancia';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const hoy = () => new Date().toISOString().slice(0, 10);

interface UsuarioLite { id: string; nombre: string; apellido: string; rol: string; }
const ROLES_FUNCIONARIO_SEGUNDA = ['SEGUNDA_INSTANCIA', 'DIRECTOR_SEGUNDA_INSTANCIA'];
async function funcionariosSegunda(): Promise<UsuarioLite[]> {
  const usuarios = await api<UsuarioLite[]>('/usuarios');
  return usuarios.filter((u) => ROLES_FUNCIONARIO_SEGUNDA.includes(u.rol));
}

// ---- Definición de campos y acciones (patrón compartido) ------------------

type CampoTipo = 'text' | 'date' | 'textarea' | 'bool' | 'select';
interface Campo {
  name: string;
  label: string;
  tipo: CampoTipo;
  required?: boolean;
  opciones?: string[];
  placeholder?: string;
  defecto?: string | boolean;
  visible?: (v: Record<string, string | boolean>) => boolean;
}

interface AccionDef {
  key: string;
  label: string;
  descripcion: string;
  icon: React.ReactNode;
  soloDecisor?: boolean; // requiere puedeDecidir2a
  // ejecuta usando el id del registro de segunda instancia (o del expediente para recepción).
  ejecutar: (exp: ExpDetalle, segundaId: string, valores: Record<string, string | boolean>) => Promise<unknown>;
  campos: Campo[];
}

function acciones(): AccionDef[] {
  return [
    {
      key: 'recepcion', label: 'Recepción', icon: <FileInput className="size-4" />,
      descripcion: 'Registra la recepción del expediente remitido a segunda instancia.',
      campos: [
        { name: 'fecha', label: 'Fecha de recepción', tipo: 'date', required: true, defecto: hoy() },
        { name: 'autoridadRemitente', label: 'Autoridad remitente (opcional)', tipo: 'text' },
        { name: 'radicadoPrimera', label: 'Radicado de primera instancia (opcional)', tipo: 'text' },
        { name: 'constanciaKey', label: 'Constancia (key, opcional)', tipo: 'text' },
        { name: 'autoConcedeKey', label: 'Auto que concede (key, opcional)', tipo: 'text' },
        { name: 'recursoKey', label: 'Recurso (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (exp, _segundaId, v) => recepcionSegundaInstancia(exp.id, {
        fecha: v.fecha as string,
        ...(v.autoridadRemitente ? { autoridadRemitente: v.autoridadRemitente as string } : {}),
        ...(v.radicadoPrimera ? { radicadoPrimera: v.radicadoPrimera as string } : {}),
        ...(v.constanciaKey ? { constanciaKey: v.constanciaKey as string } : {}),
        ...(v.autoConcedeKey ? { autoConcedeKey: v.autoConcedeKey as string } : {}),
        ...(v.recursoKey ? { recursoKey: v.recursoKey as string } : {}),
      }),
    },
    {
      key: 'decision', label: 'Decisión de segunda instancia', icon: <Gavel className="size-4" />,
      descripcion: 'Confirma, revoca, modifica o declara nulidad del fallo de primera instancia.',
      soloDecisor: true,
      campos: [
        { name: 'decision', label: 'Decisión', tipo: 'select', required: true, opciones: [...DECISION_SEGUNDA], defecto: 'CONFIRMA' },
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'aspectosModificados', label: 'Aspectos modificados', tipo: 'textarea', visible: (v) => v.decision === 'MODIFICA' },
        { name: 'retornaA', label: 'Retorna a', tipo: 'select', opciones: [...RETORNA_A], visible: (v) => v.decision === 'DECLARA_NULIDAD' },
        { name: 'tipoNulidad', label: 'Tipo de nulidad', tipo: 'text', visible: (v) => v.decision === 'DECLARA_NULIDAD' },
        { name: 'decisionKey', label: 'Decisión (key, opcional)', tipo: 'text' },
      ],
      ejecutar: (_exp, segundaId, v) => decisionSegundaInstancia(segundaId, {
        decision: v.decision as DecisionSegunda,
        fecha: v.fecha as string,
        ...(v.aspectosModificados ? { aspectosModificados: v.aspectosModificados as string } : {}),
        ...(v.retornaA ? { retornaA: v.retornaA as RetornaA } : {}),
        ...(v.tipoNulidad ? { tipoNulidad: v.tipoNulidad as string } : {}),
        ...(v.decisionKey ? { decisionKey: v.decisionKey as string } : {}),
      }),
    },
    {
      key: 'notificar', label: 'Notificar decisión', icon: <Send className="size-4" />,
      descripcion: 'Notifica la decisión de segunda instancia (personal o por edicto).',
      soloDecisor: true,
      campos: [
        { name: 'notificacionPersonal', label: 'Notificación personal', tipo: 'bool', defecto: true },
        { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
        { name: 'notificacionKey', label: 'Notificación (key, opcional)', tipo: 'text', visible: (v) => !!v.notificacionPersonal },
        { name: 'edictoKey', label: 'Edicto (key, opcional)', tipo: 'text', visible: (v) => !v.notificacionPersonal },
      ],
      ejecutar: (_exp, segundaId, v) => notificarSegundaInstancia(segundaId, {
        notificacionPersonal: !!v.notificacionPersonal,
        fecha: v.fecha as string,
        ...(v.notificacionKey ? { notificacionKey: v.notificacionKey as string } : {}),
        ...(v.edictoKey ? { edictoKey: v.edictoKey as string } : {}),
      }),
    },
    {
      key: 'ejecutoria', label: 'Ejecutoria', icon: <ShieldCheck className="size-4" />,
      descripcion: 'Registra la ejecutoria de la decisión de segunda instancia.',
      soloDecisor: true,
      campos: [{ name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() }],
      ejecutar: (_exp, segundaId, v) => ejecutoriaSegundaInstancia(segundaId, { fecha: v.fecha as string }),
    },
  ];
}

// ===========================================================================
// Componente principal del panel
// ===========================================================================

export default function PanelSegundaInstancia({ exp, onCambio }: { exp: ExpDetalle; onCambio: () => void }) {
  const [accion, setAccion] = useState<AccionDef | null>(null);
  const [reparto, setReparto] = useState(false);

  const rol = usuarioActual()?.rol;
  const estado = exp.estado_actual || '';
  const segundaId = idSegundaInstancia(exp);
  const yaRecibido = tieneSegundaInstancia(exp);
  const esDecisor = puedeDecidir2a(rol);
  const esDirector = puedeRepartir2a(rol);

  const lista = acciones();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>Acciones de segunda instancia</CardTitle>
        {esDirector && yaRecibido && (
          <Button size="sm" onClick={() => setReparto(true)}>
            <Send className="size-4" /> Repartir a funcionario
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Acciones disponibles según el estado actual (<Badge variant="secondary">{estado}</Badge>).
          {yaRecibido ? <> Recepción registrada.</> : <> Sin recepción registrada.</>}
          {' '}Los bloqueos procesales del backend se muestran como error.
        </p>
        <Separator />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {lista.map((a) => {
            // Recepción solo tiene sentido si aún no se ha recibido; se oculta cuando ya está.
            if (a.key === 'recepcion' && yaRecibido) return null;
            if (a.key !== 'recepcion' && !yaRecibido) return null;
            if (a.soloDecisor && !esDecisor) return null;
            return (
              <button
                key={a.key}
                onClick={() => setAccion(a)}
                className="flex flex-col items-start gap-1 rounded-lg border p-4 text-left transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <span className="flex items-center gap-2 text-sm font-medium">{a.icon} {a.label}</span>
                <span className="text-xs text-muted-foreground">{a.descripcion}</span>
              </button>
            );
          })}
          {!yaRecibido && !lista.some((a) => a.key === 'recepcion') && (
            <p className="text-sm text-muted-foreground">Sin acciones disponibles.</p>
          )}
        </div>
      </CardContent>

      {accion && (
        <ModalAccion
          accion={accion}
          exp={exp}
          segundaId={segundaId}
          onClose={() => setAccion(null)}
          onListo={() => { setAccion(null); onCambio(); }}
        />
      )}
      {reparto && (
        <ModalReparto
          exp={exp}
          segundaId={segundaId}
          onClose={() => setReparto(false)}
          onListo={() => { setReparto(false); onCambio(); }}
        />
      )}
    </Card>
  );
}

// ---- Modal genérico de acción ---------------------------------------------

function ModalAccion({ accion, exp, segundaId, onClose, onListo }: {
  accion: AccionDef; exp: ExpDetalle; segundaId: string; onClose: () => void; onListo: () => void;
}) {
  const [valores, setValores] = useState<Record<string, string | boolean>>(() => {
    const v: Record<string, string | boolean> = {};
    for (const c of accion.campos) {
      if (c.defecto !== undefined) v[c.name] = c.defecto;
      else if (c.tipo === 'bool') v[c.name] = false;
      else v[c.name] = '';
    }
    return v;
  });
  const [guardando, setGuardando] = useState(false);

  function set(name: string, val: string | boolean) {
    setValores((s) => ({ ...s, [name]: val }));
  }

  const visibles = accion.campos.filter((c) => !c.visible || c.visible(valores));

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (accion.key !== 'recepcion' && !segundaId) {
      avisoError('El expediente aún no tiene recepción de segunda instancia registrada.');
      return;
    }
    for (const c of visibles) {
      if (c.required && c.tipo !== 'bool') {
        if (!String(valores[c.name] ?? '').trim()) {
          avisoError(`El campo "${c.label}" es obligatorio.`);
          return;
        }
      }
    }
    setGuardando(true);
    try {
      await accion.ejecutar(exp, segundaId, valores);
      avisoExito(`${accion.label}: acción registrada.`);
      onListo();
    } catch (err) {
      avisoError('No se pudo completar la acción', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{accion.icon} {accion.label}</DialogTitle>
          <DialogDescription>{accion.descripcion}</DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          {visibles.map((c) => (
            <div key={c.name} className="space-y-2">
              {c.tipo === 'bool' ? (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!valores[c.name]}
                    onChange={(e) => set(c.name, e.target.checked)}
                    className="size-4 accent-primary"
                  />
                  {c.label}
                </label>
              ) : c.tipo === 'select' ? (
                <>
                  <Label>{c.label}</Label>
                  <Select value={String(valores[c.name] ?? '')} onValueChange={(v) => set(c.name, v)}>
                    <SelectTrigger className="w-full"><SelectValue placeholder="— Seleccione —" /></SelectTrigger>
                    <SelectContent>
                      {(c.opciones ?? []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </>
              ) : c.tipo === 'textarea' ? (
                <>
                  <Label htmlFor={c.name}>{c.label}</Label>
                  <Textarea id={c.name} rows={3} value={String(valores[c.name] ?? '')} onChange={(e) => set(c.name, e.target.value)} />
                </>
              ) : (
                <>
                  <Label htmlFor={c.name}>{c.label}</Label>
                  <Input
                    id={c.name}
                    type={c.tipo === 'date' ? 'date' : 'text'}
                    placeholder={c.placeholder}
                    value={String(valores[c.name] ?? '')}
                    onChange={(e) => set(c.name, e.target.value)}
                  />
                </>
              )}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Procesando…' : 'Confirmar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: reparto de segunda instancia ----------------------------------

function ModalReparto({ exp, segundaId, onClose, onListo }: {
  exp: ExpDetalle; segundaId: string; onClose: () => void; onListo: () => void;
}) {
  const [funcionarios, setFuncionarios] = useState<UsuarioLite[]>([]);
  const [funcionarioId, setFuncionarioId] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    funcionariosSegunda()
      .then(setFuncionarios)
      .catch((e) => avisoError('No se pudieron cargar los funcionarios', e instanceof Error ? e.message : undefined));
  }, []);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!funcionarioId) { avisoError('Seleccione un funcionario.'); return; }
    if (!segundaId) { avisoError('El expediente aún no tiene recepción de segunda instancia registrada.'); return; }
    setGuardando(true);
    try {
      await repartoSegundaInstancia(segundaId, {
        funcionarioId,
        ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
      });
      avisoExito(`Expediente ${exp.radicado} repartido a segunda instancia.`);
      onListo();
    } catch (err) {
      avisoError('No se pudo repartir el expediente', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scale className="size-5 text-primary" /> Repartir a segunda instancia</DialogTitle>
          <DialogDescription>Radicado: <strong>{exp.radicado}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label>Funcionario de segunda instancia</Label>
            <Select value={funcionarioId} onValueChange={setFuncionarioId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="— Seleccione —" /></SelectTrigger>
              <SelectContent>
                {funcionarios.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.nombre} {f.apellido} · {f.rol}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="obs">Observaciones (opcional)</Label>
            <Textarea id="obs" rows={3} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Repartiendo…' : 'Repartir'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
